import React from "react";
import { X, Plus } from "lucide-react";

const PAIR_OPTIONS = [
  "EURUSD", "GBPUSD", "USDJPY", "USDCHF", "AUDUSD", "USDCAD", "NZDUSD",
  "EURGBP", "EURJPY", "GBPJPY", "AUDJPY", "XAUUSD"
];

export interface TradeInput {
  pair: string;
  direction: "buy" | "sell";
  entryPrice: string;
  stopLossPrice: string;
  takeProfitPrice: string;
}

interface TradeInputTableProps {
  trades: TradeInput[];
  onUpdate: (trades: TradeInput[]) => void;
}

const emptyTrade: TradeInput = {
  pair: "",
  direction: "buy",
  entryPrice: '',
  stopLossPrice: '',
  takeProfitPrice: '',
};

export const TradeInputTable: React.FC<TradeInputTableProps> = ({ trades, onUpdate }) => {
  const handleChange = (idx: number, field: keyof TradeInput, value: any) => {
    const updated = trades.map((t, i) => i === idx ? { ...t, [field]: value } : t);
    onUpdate(updated);
  };

  const addTrade = () => onUpdate([...trades, { ...emptyTrade }]);
  const removeTrade = (idx: number) => {
    if (trades.length === 1) return;
    onUpdate(trades.filter((_, i) => i !== idx));
  };

  const renderTooltip = (text: string) => (
    <span className="text-xs text-neutral-400 ml-1" title={text}>
      (i)
    </span>
  );

  return (
    <div className="space-y-4">
      {trades.map((trade, index) => (
        <div key={index} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <div className="space-y-1">
              <label className="block text-sm font-medium text-neutral-300">
                Pair
                {renderTooltip("Currency pair to trade (e.g., EURUSD)")}
              </label>
              <select
                value={trade.pair}
                onChange={(e) => handleChange(index, 'pair', e.target.value)}
                className="w-full border rounded p-2.5 text-sm md:text-base bg-neutral-800 text-white border-neutral-600"
              >
                <option value="">Select a pair</option>
                {PAIR_OPTIONS.map((pair) => (
                  <option key={pair} value={pair}>
                    {pair}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-1">
              <label className="block text-sm font-medium text-neutral-300">
                Direction
                {renderTooltip("Trade direction (buy or sell)")}
              </label>
              <select
                value={trade.direction}
                onChange={(e) => handleChange(index, 'direction', e.target.value)}
                className="w-full border rounded p-2.5 text-sm md:text-base bg-neutral-800 text-white border-neutral-600"
              >
                <option value="buy">Buy</option>
                <option value="sell">Sell</option>
              </select>
            </div>
          </div>

          <div className="space-y-2">
            <div className="grid grid-cols-4 gap-2">
              <div className="col-span-3">
                <div className="grid grid-cols-3 gap-2">
                  <div className="space-y-1">
                    <label className="block text-sm font-medium text-neutral-300">
                      Entry Price
                      {renderTooltip("Entry price for the trade")}
                    </label>
                    <input
                      type="number"
                      step="0.00001"
                      value={trade.entryPrice}
                      onChange={(e) => handleChange(index, 'entryPrice', e.target.value)}
                      placeholder="Entry"
                      className="w-full border rounded p-2.5 text-sm md:text-base bg-neutral-800 text-white border-neutral-600"
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="block text-sm font-medium text-neutral-300">
                      Stop Loss
                      {renderTooltip("Stop loss price")}
                    </label>
                    <input
                      type="number"
                      step="0.00001"
                      value={trade.stopLossPrice}
                      onChange={(e) => handleChange(index, 'stopLossPrice', e.target.value)}
                      placeholder="SL"
                      className="w-full border rounded p-2.5 text-sm md:text-base bg-neutral-800 text-white border-neutral-600"
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="block text-sm font-medium text-neutral-300">
                      Take Profit
                      {renderTooltip("Take profit price (optional)")}
                    </label>
                    <input
                      type="number"
                      step="0.00001"
                      value={trade.takeProfitPrice}
                      onChange={(e) => handleChange(index, 'takeProfitPrice', e.target.value)}
                      placeholder="TP"
                      className="w-full border rounded p-2.5 text-sm md:text-base bg-neutral-800 text-white border-neutral-600"
                    />
                  </div>
                </div>
              </div>
              <div className="flex items-end">
                <button
                  type="button"
                  onClick={() => removeTrade(index)}
                  className="w-full h-[42px] bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors flex items-center justify-center"
                  title="Remove trade"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      ))}

      <button
        type="button"
        onClick={addTrade}
        className="w-full bg-neutral-700 hover:bg-neutral-600 text-white font-medium py-2.5 px-4 rounded-lg transition-colors text-sm md:text-base flex items-center justify-center gap-2"
      >
        <Plus className="h-5 w-5" />
        Add Another Trade
      </button>
    </div>
  );
};

export default TradeInputTable;
